import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { api } from '@/api/repository/axios';
import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 코드 유효시간(5분) / 재발송 가능까지 대기시간(60초) — 백엔드 정책과 일치
const CODE_VALID_SECONDS = 300;
const RESEND_WAIT_SECONDS = 60;

const Row = styled.div`
    display: flex;
    gap: 8px;
    align-items: stretch;
`;

const StyledInput = styled.input<{ hasError?: boolean }>`
    flex: 1;
    width: 100%;
    padding: 14px 16px;
    border: none;
    border-bottom: 1px solid ${props => props.hasError ? '#dc2626' : '#e5e7eb'};
    border-radius: 0;
    font-size: 1rem;
    color: #1a1a1a;
    background-color: #fff;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;

    &:focus {
        border-bottom-color: ${props => props.hasError ? '#dc2626' : designSystem.colors.primary};
    }

    &::placeholder {
        color: #9ca3af;
    }

    &:disabled {
        background-color: #f9fafb;
        color: #6b7280;
    }

    @media (max-width: 768px) {
        padding: 12px 14px;
        font-size: 0.95rem;
    }
`;

const ActionButton = styled.button<{ done?: boolean }>`
    flex-shrink: 0;
    padding: 0 16px;
    min-width: 110px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    background-color: ${props => props.disabled ? '#e2e8f0' : (props.done ? '#10b981' : designSystem.colors.primary)};
    color: ${props => props.disabled ? '#94a3b8' : '#ffffff'};
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background-color: ${props => props.done ? '#10b981' : designSystem.colors.primaryDark};
    }

    @media (max-width: 768px) {
        min-width: 92px;
        font-size: 13px;
    }
`;

const Message = styled.p<{ color?: string }>`
    font-size: 0.85rem;
    font-weight: 600;
    color: ${props => props.color || '#6b7280'};
    margin: 4px 0 0 0;
`;

const TimerRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 6px 2px 0;
    font-size: 0.82rem;
    color: #6b7280;
`;

const TimerValue = styled.span<{ expired?: boolean }>`
    font-weight: 700;
    color: ${props => props.expired ? '#dc2626' : designSystem.colors.primary};
`;

const VerifiedBadge = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 0 16px;
    min-width: 110px;
    justify-content: center;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    background-color: #10b981;
    white-space: nowrap;
`;

interface EmailVerifyFieldProps {
    /** 현재 이메일 값 (RHF 등 부모 상태와 연결) */
    email: string;
    /** 이메일 입력 변경 콜백 */
    onChangeEmail: (value: string) => void;
    /** 인증 완료 여부 */
    verified: boolean;
    /** 인증 완료 상태 변경 콜백 */
    onVerifiedChange: (verified: boolean) => void;
    /** 입력 자체를 잠금 (예: 네이버 가입 — 사전 인증 완료) */
    locked?: boolean;
    /** 에러 강조 (빨간 밑줄) */
    hasError?: boolean;
    /** 이메일 input 의 name (scrollToError 타겟용) */
    name?: string;
    placeholder?: string;
}

const formatTime = (s: number): string => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * 회원가입 이메일 인증 필드 (비밀번호 찾기와 동일 UX).
 * - 이메일 입력 + "인증코드 받기"
 * - 코드 전송 후 6자리 코드 입력 + "인증확인", 코드 유효시간 3분 카운트다운
 * - 재발송은 60초 후 가능 (버튼에 남은 초 카운팅)
 * - 인증 성공 시 "인증완료" 상태로 전환되고 이메일 입력이 잠긴다.
 */
const EmailVerifyField = ({
    email,
    onChangeEmail,
    verified,
    onVerifiedChange,
    locked = false,
    hasError = false,
    name = 'email',
    placeholder = 'your-email@gmail.com',
}: EmailVerifyFieldProps) => {
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [message, setMessage] = useState<{ text: string; color: string } | null>(null);
    const [codeRemaining, setCodeRemaining] = useState<number>(0);
    const [resendRemaining, setResendRemaining] = useState<number>(0);

    const emailValid = EMAIL_REGEX.test(email);
    const isLocked = verified || locked;
    const expired = codeSent && codeRemaining <= 0;

    // 코드 전송 후 두 타이머(코드 유효/재발송 대기)를 매초 감소. 인증 완료 시 정지.
    useEffect(() => {
        if (!codeSent || verified) return;
        const id = window.setInterval(() => {
            setCodeRemaining((v) => Math.max(0, v - 1));
            setResendRemaining((v) => Math.max(0, v - 1));
        }, 1000);
        return () => window.clearInterval(id);
    }, [codeSent, verified]);

    const handleSend = async () => {
        if (!emailValid || sending) return;
        if (codeSent && resendRemaining > 0) return; // 재발송 throttle
        setSending(true);
        setMessage(null);
        try {
            await api.emailVerification.send({ email });
            setCodeSent(true);
            setCode('');
            setCodeRemaining(CODE_VALID_SECONDS);
            setResendRemaining(RESEND_WAIT_SECONDS);
            setMessage(null);
        } catch (error: any) {
            const text = error?.response?.data?.message || '인증코드 전송에 실패했습니다. 잠시 후 다시 시도해주세요.';
            setMessage({ text, color: '#dc2626' });
        } finally {
            setSending(false);
        }
    };

    const handleVerify = async () => {
        if (!code || verifying || expired) return;
        setVerifying(true);
        try {
            const res: any = await api.emailVerification.verify({ email, code });
            const ok = res?.code === 0 || res?.message === '성공' || res?.result === true;
            if (ok) {
                onVerifiedChange(true);
                setCodeRemaining(0);
                setResendRemaining(0);
                setMessage({ text: '이메일 인증이 완료되었습니다.', color: designSystem.colors.primary });
            } else {
                setMessage({ text: res?.message || '인증코드가 올바르지 않습니다.', color: '#dc2626' });
            }
        } catch (error: any) {
            const text = error?.response?.data?.message || '인증코드가 올바르지 않거나 만료되었습니다.';
            setMessage({ text, color: '#dc2626' });
        } finally {
            setVerifying(false);
        }
    };

    const handleEmailChange = (value: string) => {
        if (isLocked) return;
        onChangeEmail(value);
        // 이메일을 수정하면 진행 중이던 인증 절차를 초기화한다.
        if (codeSent) {
            setCodeSent(false);
            setCode('');
            setCodeRemaining(0);
            setResendRemaining(0);
        }
        if (message) {
            setMessage(null);
        }
    };

    const sendLabel = sending
        ? <ButtonSpinner />
        : !codeSent
            ? '인증코드 받기'
            : resendRemaining > 0
                ? `재발송 (${resendRemaining}s)`
                : '코드 재전송';

    return (
        <div>
            <Row>
                <StyledInput
                    name={name}
                    type="email"
                    value={email}
                    placeholder={placeholder}
                    hasError={hasError}
                    disabled={isLocked}
                    onChange={(e) => handleEmailChange(e.target.value)}
                />
                {verified ? (
                    <VerifiedBadge>인증완료</VerifiedBadge>
                ) : (
                    <ActionButton
                        type="button"
                        onClick={handleSend}
                        disabled={!emailValid || sending || locked || (codeSent && resendRemaining > 0)}
                    >
                        {sendLabel}
                    </ActionButton>
                )}
            </Row>

            {codeSent && !verified && (
                <>
                    <Row style={{ marginTop: 8 }}>
                        <StyledInput
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={code}
                            placeholder="인증코드 6자리"
                            disabled={expired}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        />
                        <ActionButton
                            type="button"
                            onClick={handleVerify}
                            disabled={!code || verifying || expired}
                        >
                            {verifying ? <ButtonSpinner /> : '인증확인'}
                        </ActionButton>
                    </Row>
                    <Message color={expired ? '#dc2626' : designSystem.colors.primary}>
                        {expired
                            ? '코드가 만료되었습니다. 재발송해주세요.'
                            : `인증코드를 전송했습니다. 메일함을 확인해주세요. (남은 시간 ${formatTime(codeRemaining)})`}
                    </Message>
                </>
            )}

            {message && <Message color={message.color}>{message.text}</Message>}
        </div>
    );
};

export default EmailVerifyField;
